import { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import {
  resolveProductImageUrl,
  normalizeOptionSetsFromForm,
  normalizeProductCategories,
  PROMOTION_DISPLAY_MODES,
  resolveColorInputToHex,
} from "./shopUtils";

// ─── Variant key generator (mirrors server logic) ────────────────────────────
const buildGeneratedVariants = (optionSets) => {
  const normalized = normalizeOptionSetsFromForm(optionSets);
  if (!normalized.length) return [{ key: "default", label: "Standard" }];

  const recurse = (index, currentEntries) => {
    if (index >= normalized.length) {
      const key = currentEntries.map((e) => `${e.name}:${e.value}`).join("|");
      const label = currentEntries.map((e) => e.value).join(" / ");
      return [{ key, label }];
    }
    const set = normalized[index];
    return set.values.flatMap((value) =>
      recurse(index + 1, [...currentEntries, { name: set.name, value }])
    );
  };

  return recurse(0, []);
};

const toDateTimeLocalInput = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const localTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
};

export default function ProductFormDialog({
  open,
  onClose,
  onSave,
  initialProduct,
  loading,
  onUploadImages,
  uploadingImages,
  categoryOptions = [],
}) {
  const initialPromotion = initialProduct?.promotion || null;
  const [name, setName] = useState(initialProduct?.name || "");
  const [categories, setCategories] = useState(() =>
    normalizeProductCategories(
      Array.isArray(initialProduct?.categories) &&
        initialProduct.categories.length
        ? initialProduct.categories
        : initialProduct?.category || ""
    )
  );
  const [categoryInput, setCategoryInput] = useState("");
  const [description, setDescription] = useState(
    initialProduct?.description || ""
  );
  const [basePrice, setBasePrice] = useState(
    String(initialProduct?.basePrice ?? "")
  );
  const [taxRate, setTaxRate] = useState(
    String(initialProduct?.taxRate ?? "0")
  );
  const [images, setImages] = useState(
    Array.isArray(initialProduct?.images) ? initialProduct.images : []
  );
  const [uploadError, setUploadError] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [isActive, setIsActive] = useState(
    typeof initialProduct?.isActive === "boolean"
      ? initialProduct.isActive
      : true
  );
  const [showVariantStockToMembers, setShowVariantStockToMembers] = useState(
    typeof initialProduct?.showVariantStockToMembers === "boolean"
      ? initialProduct.showVariantStockToMembers
      : false
  );
  const [promotionEnabled, setPromotionEnabled] = useState(
    Boolean(initialPromotion?.isEnabled)
  );
  const [promotionPercent, setPromotionPercent] = useState(
    typeof initialPromotion?.discountPercent === "number"
      ? String(initialPromotion.discountPercent)
      : ""
  );
  const [promotionValidUntil, setPromotionValidUntil] = useState(
    toDateTimeLocalInput(initialPromotion?.validUntil)
  );
  const [promotionDisplayMode, setPromotionDisplayMode] = useState(() => {
    const candidate = String(initialPromotion?.displayMode || "")
      .trim()
      .toLowerCase();
    return Object.values(PROMOTION_DISPLAY_MODES).includes(candidate)
      ? candidate
      : PROMOTION_DISPLAY_MODES.COUNTDOWN;
  });

  // ── Option sets with extended shape (name, valuesText, isColorSet, colorMap) ──
  const [optionSets, setOptionSets] = useState(() => {
    if (
      !Array.isArray(initialProduct?.optionSets) ||
      !initialProduct.optionSets.length
    ) {
      return [{ name: "", valuesText: "", isColorSet: false, colorMap: {} }];
    }
    return initialProduct.optionSets.map((set) => ({
      name: set.name,
      valuesText: Array.isArray(set.values) ? set.values.join(", ") : "",
      isColorSet: Boolean(set.isColorSet),
      colorMap:
        set.colorMap && typeof set.colorMap === "object"
          ? { ...set.colorMap }
          : {},
    }));
  });

  // ── Per-variant price / active overrides ──
  const [variantOverrides, setVariantOverrides] = useState(() => {
    if (
      !Array.isArray(initialProduct?.variants) ||
      !initialProduct.variants.length
    )
      return {};
    return initialProduct.variants.reduce((acc, v) => {
      if (v.key) {
        acc[v.key] = {
          price: String(v.price ?? ""),
          isActive: typeof v.isActive === "boolean" ? v.isActive : true,
          stock:
            v.stock === null || typeof v.stock === "undefined"
              ? ""
              : String(v.stock),
        };
      }
      return acc;
    }, {});
  });

  // ── Computed variants ──
  const generatedVariants = useMemo(
    () => buildGeneratedVariants(optionSets),
    [optionSets]
  );

  // ── Option set helpers ──
  const updateOptionSet = (index, patch) => {
    setOptionSets((prev) =>
      prev.map((set, i) => (i === index ? { ...set, ...patch } : set))
    );
  };

  const removeOptionSet = (index) => {
    setOptionSets((prev) => prev.filter((_, i) => i !== index));
  };

  const setOptionColorMapValue = (setIndex, value, hexColor) => {
    setOptionSets((prev) =>
      prev.map((set, i) =>
        i === setIndex
          ? { ...set, colorMap: { ...set.colorMap, [value]: hexColor } }
          : set
      )
    );
  };

  // ── Variant override helpers ──
  const setVariantPrice = (key, priceText) => {
    setVariantOverrides((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        price: priceText,
        isActive: prev[key]?.isActive ?? true,
        stock: prev[key]?.stock ?? "",
      },
    }));
  };

  const setVariantActive = (key, active) => {
    setVariantOverrides((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        isActive: active,
        price: prev[key]?.price ?? String(basePrice),
        stock: prev[key]?.stock ?? "",
      },
    }));
  };

  const setVariantStock = (key, stockText) => {
    setVariantOverrides((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        stock: stockText,
        isActive: prev[key]?.isActive ?? true,
        price: prev[key]?.price ?? String(basePrice),
      },
    }));
  };

  // ── Image handlers ──
  const handleImageTextChange = (event) => {
    const nextImages = String(event.target.value || "")
      .split("\n")
      .map((v) => v.trim())
      .filter(Boolean);
    setImages(nextImages);
  };

  const handleRemoveImage = (image) => {
    setImages((prev) => prev.filter((item) => item !== image));
  };

  const moveImage = (fromIndex, toIndex) => {
    setImages((prev) => {
      if (
        !Array.isArray(prev) ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= prev.length ||
        toIndex >= prev.length
      ) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleUploadChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length || !onUploadImages) return;
    setUploadError("");

    const invalidFile = files.find((file) => !file.type.startsWith("image/"));
    if (invalidFile) {
      setUploadError("Bitte nur Bilddateien hochladen.");
      event.target.value = "";
      return;
    }
    const oversizedFile = files.find((file) => file.size > 5 * 1024 * 1024);
    if (oversizedFile) {
      setUploadError("Ein Bild ist zu groß (max. 5 MB).");
      event.target.value = "";
      return;
    }

    try {
      const uploaded = await onUploadImages(files);
      if (uploaded?.length) {
        setImages((prev) => Array.from(new Set([...prev, ...uploaded])));
      }
    } catch (error) {
      setUploadError(
        error?.data?.message || "Upload fehlgeschlagen. Bitte erneut versuchen."
      );
    } finally {
      event.target.value = "";
    }
  };

  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = (event) => event.preventDefault();
  const handleDrop = (index) => {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      return;
    }
    moveImage(dragIndex, index);
    setDragIndex(null);
  };

  const promotionPercentValue = Number(promotionPercent);
  const promotionPercentValid =
    !promotionEnabled ||
    (Number.isFinite(promotionPercentValue) &&
      promotionPercentValue > 0 &&
      promotionPercentValue <= 100);

  const promotionValidUntilDate = promotionValidUntil
    ? new Date(promotionValidUntil)
    : null;
  const promotionDateValid =
    !promotionEnabled ||
    Boolean(
      promotionValidUntilDate &&
        !Number.isNaN(promotionValidUntilDate.getTime()) &&
        promotionValidUntilDate.getTime() > Date.now()
    );

  const promotionPayload = promotionEnabled
    ? {
        isEnabled: true,
        discountPercent: promotionPercentValue,
        validUntil: promotionValidUntilDate?.toISOString(),
        displayMode: promotionDisplayMode,
      }
    : {
        isEnabled: false,
        discountPercent: 0,
        validUntil: null,
        displayMode: promotionDisplayMode,
      };

  const parsedTaxRate = Number(taxRate);
  const taxRateValid =
    Number.isFinite(parsedTaxRate) &&
    parsedTaxRate >= 0 &&
    parsedTaxRate <= 100;
  const computedGrossPrice =
    Number.isFinite(Number(basePrice)) && Number(basePrice) >= 0 && taxRateValid
      ? Math.round(
          (Number(basePrice) * (1 + parsedTaxRate / 100) + Number.EPSILON) * 100
        ) / 100
      : null;

  const handleCategoriesChange = (_, nextValue) => {
    setCategories(normalizeProductCategories(nextValue));
  };

  const handleCategoryInputKeyDown = (event) => {
    if (event.key !== "," && event.key !== ";") return;
    if (!String(categoryInput || "").trim()) return;

    event.preventDefault();
    setCategories((prev) =>
      normalizeProductCategories([...prev, categoryInput])
    );
    setCategoryInput("");
  };

  // ── Save ──
  const handleSave = () => {
    const normalizedOptionSets = normalizeOptionSetsFromForm(optionSets);
    const normalizedCategories = normalizeProductCategories([
      ...categories,
      categoryInput,
    ]);

    // Build variants payload with price/active overrides
    const variants = generatedVariants.map(({ key, label }) => {
      const override = variantOverrides[key];
      const rawPrice = override?.price;
      const price =
        rawPrice !== undefined && rawPrice !== ""
          ? Number(rawPrice)
          : Number(basePrice);
      const rawStock = override?.stock;
      const normalizedStock =
        rawStock === undefined ||
        rawStock === null ||
        String(rawStock).trim() === ""
          ? null
          : Number(rawStock);
      return {
        key,
        label,
        price: Number.isFinite(price) && price >= 0 ? price : Number(basePrice),
        isActive: override?.isActive ?? true,
        stock:
          normalizedStock !== null &&
          Number.isInteger(normalizedStock) &&
          normalizedStock >= 0
            ? normalizedStock
            : null,
      };
    });

    onSave({
      id: initialProduct?._id,
      name: name.trim(),
      categories: normalizedCategories,
      description: description.trim(),
      basePrice: Number(basePrice),
      taxRate: taxRateValid ? parsedTaxRate : 0,
      images: Array.isArray(images) ? images : [],
      optionSets: normalizedOptionSets,
      variants,
      promotion: promotionPayload,
      showVariantStockToMembers,
      isActive,
    });
  };

  // ── Validation ──
  const variantPricesValid = generatedVariants.every(({ key }) => {
    const price = variantOverrides[key]?.price;
    if (price === undefined || price === "") return true;
    return Number(price) >= 0 && Number.isFinite(Number(price));
  });
  const variantStocksValid = generatedVariants.every(({ key }) => {
    const stock = variantOverrides[key]?.stock;
    if (stock === undefined || stock === null || stock === "") return true;
    return Number(stock) >= 0 && Number.isInteger(Number(stock));
  });

  const canSave =
    name.trim().length > 0 &&
    Number.isFinite(Number(basePrice)) &&
    Number(basePrice) >= 0 &&
    variantPricesValid &&
    variantStocksValid &&
    promotionPercentValid &&
    promotionDateValid;

  const isSimpleProduct =
    generatedVariants.length === 1 && generatedVariants[0].key === "default";

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialProduct?._id ? "Produkt bearbeiten" : "Produkt erstellen"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* ── Basic fields ── */}
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
          />
          <Autocomplete
            multiple
            freeSolo
            disableCloseOnSelect
            options={categoryOptions}
            filterSelectedOptions
            value={categories}
            inputValue={categoryInput}
            onChange={handleCategoriesChange}
            onInputChange={(_, nextValue) => setCategoryInput(nextValue || "")}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={`${option}-${index}`}
                  label={option}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Kategorien"
                helperText="Optional. Bestehende Kategorien koennen ausgewaehlt oder neue eingetippt werden. Mehrere Kategorien mit Komma oder Semikolon trennen, z. B. Kleidung; Trainingsequipment."
                onKeyDown={handleCategoryInputKeyDown}
                fullWidth
              />
            )}
          />
          <TextField
            label="Beschreibung"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems="flex-start"
          >
            <TextField
              type="number"
              label="Nettopreis (EUR)"
              value={basePrice}
              onChange={(event) => setBasePrice(event.target.value)}
              sx={{ flex: 1 }}
              helperText="Netto-Preis für alle Varianten (kann pro Variante überschrieben werden)"
              inputProps={{ min: 0, step: 0.01 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
            />
            <TextField
              type="number"
              label="Umsatzsteuer"
              value={taxRate}
              onChange={(event) => setTaxRate(event.target.value)}
              sx={{ flex: 0.7 }}
              helperText="MwSt.-Satz in Prozent (z. B. 19)"
              error={taxRate !== "" && !taxRateValid}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
            <TextField
              label="Bruttopreis (EUR)"
              value={
                computedGrossPrice !== null
                  ? computedGrossPrice.toLocaleString("de-DE", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : "–"
              }
              sx={{ flex: 1 }}
              helperText="Endpreis inkl. MwSt. (automatisch berechnet)"
              InputProps={{
                readOnly: true,
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
            />
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              borderColor: promotionEnabled ? "error.light" : "divider",
              background: promotionEnabled
                ? "linear-gradient(135deg, rgba(239,68,68,0.08) 0%, rgba(249,115,22,0.03) 100%)"
                : "background.paper",
            }}
          >
            <Stack spacing={1.25}>
              <FormControlLabel
                control={
                  <Switch
                    checked={promotionEnabled}
                    onChange={(event) =>
                      setPromotionEnabled(event.target.checked)
                    }
                  />
                }
                label="Zeitlich begrenzten Rabatt aktivieren"
              />

              {promotionEnabled ? (
                <>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
                    <TextField
                      type="number"
                      label="Rabatt in %"
                      value={promotionPercent}
                      onChange={(event) =>
                        setPromotionPercent(event.target.value)
                      }
                      error={!promotionPercentValid}
                      helperText={
                        !promotionPercentValid
                          ? "Bitte einen Wert zwischen 0,01 und 100 eingeben."
                          : "Der Rabatt wird auf alle aktiven Varianten angewendet."
                      }
                      sx={{ flex: 1 }}
                      inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      type="datetime-local"
                      label="Gültig bis"
                      value={promotionValidUntil}
                      onChange={(event) =>
                        setPromotionValidUntil(event.target.value)
                      }
                      error={!promotionDateValid}
                      helperText={
                        !promotionDateValid
                          ? "Bitte einen Zeitpunkt in der Zukunft wählen."
                          : "Nach Ablauf endet die Aktion automatisch."
                      }
                      sx={{ flex: 1 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>

                  <TextField
                    select
                    label="Hinweis-Darstellung auf der Produktkarte"
                    value={promotionDisplayMode}
                    onChange={(event) =>
                      setPromotionDisplayMode(event.target.value)
                    }
                    fullWidth
                  >
                    <MenuItem value={PROMOTION_DISPLAY_MODES.COUNTDOWN}>
                      Countdown (Tage, Stunden, Minuten, Sekunden)
                    </MenuItem>
                    <MenuItem value={PROMOTION_DISPLAY_MODES.DATE}>
                      Text: Nur gültig bis Datum/Uhrzeit
                    </MenuItem>
                    <MenuItem value={PROMOTION_DISPLAY_MODES.SHORT}>
                      Text: Nur für kurze Zeit!
                    </MenuItem>
                  </TextField>
                </>
              ) : null}
            </Stack>
          </Paper>

          {/* ── Images ── */}
          <TextField
            label="Bild-URLs (eine URL pro Zeile)"
            value={(images || []).join("\n")}
            onChange={handleImageTextChange}
            fullWidth
            multiline
            minRows={3}
            helperText="Optional. Du kannst das Produkt auch ohne Bild speichern."
          />
          <Stack spacing={1}>
            <Button
              variant="outlined"
              component="label"
              startIcon={
                <Iconify icon="eva:cloud-upload-fill" width={18} height={18} />
              }
              disabled={uploadingImages}
            >
              {uploadingImages
                ? "Bilder werden hochgeladen..."
                : "Bilder hochladen"}
              <input
                hidden
                type="file"
                accept="image/*"
                multiple
                onChange={handleUploadChange}
              />
            </Button>
            {uploadError ? <Alert severity="error">{uploadError}</Alert> : null}
            {images?.length ? (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {images.map((image, index) => (
                  <Paper
                    key={image}
                    variant="outlined"
                    sx={{
                      p: 0.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      borderRadius: 1.5,
                      borderColor:
                        dragIndex === index ? "primary.main" : "divider",
                      opacity: dragIndex === index ? 0.6 : 1,
                    }}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(index)}
                  >
                    <Box
                      component="img"
                      src={resolveProductImageUrl(image)}
                      alt="Produktbild"
                      sx={{
                        width: 56,
                        height: 56,
                        objectFit: "cover",
                        borderRadius: 1,
                      }}
                    />
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => moveImage(index, Math.max(0, index - 1))}
                        disabled={index === 0}
                      >
                        <Iconify
                          icon="eva:arrow-left-fill"
                          width={16}
                          height={16}
                        />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          moveImage(
                            index,
                            Math.min(images.length - 1, index + 1)
                          )
                        }
                        disabled={index === images.length - 1}
                      >
                        <Iconify
                          icon="eva:arrow-right-fill"
                          width={16}
                          height={16}
                        />
                      </IconButton>
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(image)}
                    >
                      <Iconify
                        icon="eva:trash-2-outline"
                        width={16}
                        height={16}
                      />
                    </IconButton>
                  </Paper>
                ))}
              </Stack>
            ) : null}
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(isActive)}
                onChange={(event) => setIsActive(event.target.checked)}
              />
            }
            label="Produkt aktiv"
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(showVariantStockToMembers)}
                onChange={(event) =>
                  setShowVariantStockToMembers(event.target.checked)
                }
              />
            }
            label="Bestand für Mitglieder anzeigen"
          />

          <Divider />

          {/* ── Option sets ── */}
          <Typography variant="h6">Varianten-Optionen</Typography>
          <Typography variant="body2" color="text.secondary">
            Definiere Auswahlmöglichkeiten für deine Kunden. Beispiel:
            Optionsname &quot;Farbe&quot; mit Werten &quot;Rot, Blau&quot; und
            Optionsname &quot;Größe&quot; mit Werten &quot;S, M, L&quot;.
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              borderColor: "rgba(2,136,209,0.25)",
              background:
                "linear-gradient(135deg, rgba(2,136,209,0.1) 0%, rgba(2,136,209,0.02) 75%)",
            }}
          >
            <Stack spacing={1.2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify icon="eva:color-palette-fill" width={18} height={18} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  So nutzt du die Option &bdquo;Farbe&ldquo; richtig
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Aktiviere den Schalter nur bei echten Farbwerten. Dann sieht der
                Kunde statt reinem Text direkt Farbpunkte im Shop.
              </Typography>
              <Stack spacing={0.75}>
                {[
                  "1. Erstelle eine Option, z. B. „Farbe“.",
                  "2. Trage Werte ein, z. B. „Rot, Blau, Schwarz“.",
                  "3. Aktiviere den Schalter „Farbe“.",
                  "4. Weise jedem Wert eine Farbe per Picker oder Hex/Name zu.",
                ].map((tip) => (
                  <Stack
                    key={tip}
                    direction="row"
                    spacing={0.75}
                    alignItems="flex-start"
                  >
                    <Iconify
                      icon="eva:checkmark-circle-2-fill"
                      width={16}
                      height={16}
                      sx={{ color: "info.main", mt: 0.25, flexShrink: 0 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {tip}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Paper>

          {optionSets.map((set, index) => {
            // Derive current values from valuesText for color input rendering
            const currentValues = String(set.valuesText || "")
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean);

            return (
              <Box key={`optset-${index}`}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  alignItems="flex-start"
                >
                  <TextField
                    label="Optionsname"
                    value={set.name}
                    onChange={(event) =>
                      updateOptionSet(index, { name: event.target.value })
                    }
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Werte (Komma-getrennt)"
                    value={set.valuesText}
                    onChange={(event) =>
                      updateOptionSet(index, { valuesText: event.target.value })
                    }
                    sx={{ flex: 2 }}
                  />
                  <Tooltip title="Als Farb-Option markieren (zeigt Farbvorschau im Katalog)">
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(set.isColorSet)}
                          onChange={(event) =>
                            updateOptionSet(index, {
                              isColorSet: event.target.checked,
                            })
                          }
                        />
                      }
                      label="Farbe"
                      sx={{ m: 0, whiteSpace: "nowrap", alignSelf: "center" }}
                    />
                  </Tooltip>
                  <IconButton
                    color="error"
                    onClick={() => removeOptionSet(index)}
                    disabled={optionSets.length === 1}
                    sx={{ alignSelf: "center" }}
                  >
                    <Iconify
                      icon="eva:trash-2-outline"
                      width={18}
                      height={18}
                    />
                  </IconButton>
                </Stack>

                {/* Color hex inputs — shown only when isColorSet is active and values exist */}
                {set.isColorSet && currentValues.length > 0 ? (
                  <Box sx={{ mt: 1, ml: { md: 1 } }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.75, display: "block" }}
                    >
                      Farbe pro Wert: Farbpicker oder Name/Hex eingeben (z. B.
                      blau, gruen, weiss, #2563eb)
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      {currentValues.map((value) => {
                        const rawColorValue = set.colorMap?.[value] || "";
                        const resolvedHex =
                          resolveColorInputToHex(rawColorValue);
                        const hasInput =
                          String(rawColorValue || "").trim().length > 0;
                        const validColor = Boolean(resolvedHex);
                        const pickerValue = validColor
                          ? resolvedHex
                          : "#2563eb";
                        return (
                          <Stack
                            key={value}
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                          >
                            <Box
                              sx={{
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                bgcolor: validColor ? resolvedHex : "grey.200",
                                border: "1.5px solid",
                                borderColor: validColor
                                  ? "transparent"
                                  : "divider",
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{ minWidth: 32, color: "text.secondary" }}
                            >
                              {value}
                            </Typography>
                            <Box
                              component="input"
                              type="color"
                              aria-label={`Farbpicker für ${value}`}
                              value={pickerValue}
                              onChange={(e) =>
                                setOptionColorMapValue(
                                  index,
                                  value,
                                  e.target.value
                                )
                              }
                              sx={{
                                width: 34,
                                height: 34,
                                border: "none",
                                p: 0,
                                bgcolor: "transparent",
                                cursor: "pointer",
                                "&::-webkit-color-swatch-wrapper": { p: 0 },
                                "&::-webkit-color-swatch": {
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 0.75,
                                },
                              }}
                            />
                            <TextField
                              size="small"
                              placeholder="blau oder #2563eb"
                              value={rawColorValue}
                              onChange={(e) =>
                                setOptionColorMapValue(
                                  index,
                                  value,
                                  e.target.value
                                )
                              }
                              error={hasInput && !validColor}
                              sx={{ width: 170 }}
                            />
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                ) : null}
              </Box>
            );
          })}

          <Button
            variant="outlined"
            onClick={() =>
              setOptionSets((prev) => [
                ...prev,
                { name: "", valuesText: "", isColorSet: false, colorMap: {} },
              ])
            }
            startIcon={<Iconify icon="eva:plus-fill" width={18} height={18} />}
          >
            Option hinzufügen
          </Button>

          {/* ── Variant matrix ── */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Varianten, Preise & Bestand ({generatedVariants.length})
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1.25, display: "block" }}
            >
              Leeres Bestandsfeld bedeutet unbegrenzter Bestand. Klick auf den
              Status, um eine Variante direkt zu aktivieren oder zu
              deaktivieren.
            </Typography>

            {isSimpleProduct ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.25 }}
              >
                Dieses Produkt hat keine Auswahlvarianten. Nutze die Zeile
                &bdquo;Standard&ldquo; für Preis und Bestand.
              </Typography>
            ) : null}

            <Stack spacing={0.75}>
              {/* Header row */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 1, display: { xs: "none", sm: "flex" } }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flex: 1 }}
                >
                  Variante
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ width: 130 }}
                >
                  Netto (EUR)
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ width: 120 }}
                >
                  Bestand
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ width: 148 }}
                >
                  Status
                </Typography>
              </Stack>
              <Divider />
              {generatedVariants.map(({ key, label }) => {
                const override = variantOverrides[key];
                const priceVal =
                  override?.price !== undefined
                    ? override.price
                    : String(basePrice);
                const stockVal =
                  override?.stock !== undefined ? override.stock : "";
                const isVariantActive =
                  override?.isActive !== undefined ? override.isActive : true;
                const priceInvalid =
                  priceVal !== "" &&
                  (!Number.isFinite(Number(priceVal)) || Number(priceVal) < 0);
                const stockInvalid =
                  stockVal !== "" &&
                  (!Number.isInteger(Number(stockVal)) || Number(stockVal) < 0);

                return (
                  <Stack
                    key={key}
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    spacing={1}
                    sx={{
                      px: 1,
                      py: 0.9,
                      borderRadius: 1,
                      bgcolor: isVariantActive ? "transparent" : "action.hover",
                      opacity: isVariantActive ? 1 : 0.6,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Chip
                        label={label}
                        size="small"
                        variant="outlined"
                        sx={{ maxWidth: "100%" }}
                      />
                    </Box>
                    <TextField
                      size="small"
                      type="number"
                      value={priceVal}
                      onChange={(e) => setVariantPrice(key, e.target.value)}
                      error={priceInvalid}
                      placeholder={String(basePrice)}
                      sx={{ width: { xs: "100%", sm: 130 } }}
                      inputProps={{ min: 0, step: 0.01 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">€</InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      value={stockVal}
                      onChange={(e) => setVariantStock(key, e.target.value)}
                      error={stockInvalid}
                      placeholder="Unbegrenzt"
                      sx={{ width: { xs: "100%", sm: 120 } }}
                      inputProps={{ min: 0, step: 1 }}
                    />
                    <Button
                      variant={isVariantActive ? "contained" : "outlined"}
                      color={isVariantActive ? "success" : "inherit"}
                      onClick={() => setVariantActive(key, !isVariantActive)}
                      startIcon={
                        <Iconify
                          icon={
                            isVariantActive
                              ? "eva:checkmark-circle-2-fill"
                              : "eva:slash-fill"
                          }
                          width={16}
                          height={16}
                        />
                      }
                      sx={{
                        width: { xs: "100%", sm: 148 },
                        flexShrink: 0,
                        fontWeight: 700,
                        justifyContent: "flex-start",
                        ...(isVariantActive
                          ? {}
                          : {
                              color: "text.secondary",
                              borderColor: "divider",
                              bgcolor: "background.paper",
                            }),
                      }}
                    >
                      {isVariantActive ? "Aktiv" : "Inaktiv"}
                    </Button>
                  </Stack>
                );
              })}
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !canSave}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}
